import { useCallback, useEffect, useReducer, useRef, useState } from "react";

import { SELECTION_DEBOUNCE_MS } from "../constants/payoutBatches.constants";
import { PayoutBatchesService } from "../services/payoutBatches.service";
import {
  type PayoutBatchDetail,
  type PayoutBatchItem,
  PayoutBatchItemStatus,
  type PayoutBatchSelectionOp,
} from "../types/payoutBatches.types";
import { type BatchError, classifyBatchError } from "../utils/batchErrors";
import {
  hasPendingSelection,
  INITIAL_SELECTION_STATE,
  isSelectedWithOverlay,
  selectionOps,
  selectionReducer,
  settledStatuses,
} from "../utils/selectionReducer";
import type { UsePayoutBatchItemsResult } from "./usePayoutBatchItems";

export type BulkSelectionOp = Exclude<PayoutBatchSelectionOp, "select" | "deselect">;

export interface UsePayoutBatchSelectionResult {
  /** The row as the person sees it — the pending toggle wins over the server until it is confirmed. */
  isSelected: (item: PayoutBatchItem) => boolean;
  toggle: (item: PayoutBatchItem, selected: boolean) => void;
  /** `select_all_valid`, `deselect_all`, `exclude_with_warnings`, or `replace` with the ids to keep. */
  bulk: (op: BulkSelectionOp, itemIds?: string[]) => Promise<BatchError | null>;
  /** A PATCH is pending or in flight — the confirm button waits for the server's fingerprint. */
  isSyncing: boolean;
  /** Row toggles stay open while single toggles sync (the reducer queues them); only a bulk op locks them. */
  isBulkPending: boolean;
  error: BatchError | null;
}

/**
 * Optimistic row selection over `PATCH /:id/selection`. Toggles are coalesced for 300 ms into at most one
 * `select` and one `deselect` request; the batch each answer carries (new totals, new fingerprint) is adopted
 * and the toggled rows are patched to their new status in the same render, so a checkbox never falls back to
 * the old status while the page re-reads. Bulk ops go out at once. Mount the consumer keyed by batch id: the
 * state is not reset when the id changes.
 */
export function usePayoutBatchSelection(
  batch: PayoutBatchDetail | null,
  applyBatch: (next: PayoutBatchDetail) => void,
  items: Pick<UsePayoutBatchItemsResult, "refresh" | "patchStatuses">,
): UsePayoutBatchSelectionResult {
  const [state, dispatch] = useReducer(selectionReducer, INITIAL_SELECTION_STATE);
  const [bulkPending, setBulkPending] = useState(false);
  const [error, setError] = useState<BatchError | null>(null);
  const batchId = batch?.id ?? null;

  const applyRef = useRef(applyBatch);
  const itemsRef = useRef(items);
  useEffect(() => {
    applyRef.current = applyBatch;
    itemsRef.current = items;
  });

  const pendingSignature = Object.entries(state.pending)
    .map(([id, direction]) => `${id}:${direction}`)
    .sort()
    .join(",");

  useEffect(() => {
    if (!pendingSignature) return;
    const timer = setTimeout(() => dispatch({ type: "flush" }), SELECTION_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [pendingSignature]);

  useEffect(() => {
    const inFlight = state.inFlight;
    if (!inFlight || !batchId) return;
    let cancelled = false;
    void (async () => {
      try {
        let latest: PayoutBatchDetail | null = null;
        for (const op of selectionOps(inFlight)) {
          const { data } = await PayoutBatchesService.updateSelection(batchId, op.op, op.item_ids);
          latest = data;
        }
        if (cancelled) return;
        // Adopting the batch bumps its revision, which re-reads the page in the background.
        if (latest) applyRef.current(latest);
        itemsRef.current.patchStatuses(settledStatuses(inFlight));
        setError(null);
        dispatch({ type: "settled" });
      } catch (err: unknown) {
        if (cancelled) return;
        setError(classifyBatchError(err));
        dispatch({ type: "rollback" });
        void itemsRef.current.refresh();
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [state.inFlight, batchId]);

  const isSelected = useCallback(
    (item: PayoutBatchItem) =>
      isSelectedWithOverlay(state, item.id, item.status === PayoutBatchItemStatus.SELECTED),
    [state],
  );

  const toggle = useCallback((item: PayoutBatchItem, selected: boolean) => {
    dispatch({
      type: "toggle",
      id: item.id,
      selected,
      serverSelected: item.status === PayoutBatchItemStatus.SELECTED,
    });
  }, []);

  const bulk = useCallback(
    async (op: BulkSelectionOp, itemIds?: string[]): Promise<BatchError | null> => {
      if (!batchId) return null;
      setBulkPending(true);
      try {
        const { data } = await PayoutBatchesService.updateSelection(batchId, op, itemIds);
        applyRef.current(data);
        dispatch({ type: "reset" });
        setError(null);
        return null;
      } catch (err: unknown) {
        const classified = classifyBatchError(err);
        setError(classified);
        void itemsRef.current.refresh();
        return classified;
      } finally {
        setBulkPending(false);
      }
    },
    [batchId],
  );

  return {
    isSelected,
    toggle,
    bulk,
    isSyncing: hasPendingSelection(state) || bulkPending,
    isBulkPending: bulkPending,
    error,
  };
}
